import React, { useEffect, useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';
import throttle from 'lodash/throttle';
import { isAuthenticated } from '../../../../services/auth';
import { getUsers, deleteUser, banUser, unBanUser, changeUserPassword } from '../../../../services/admin';
import { userActiveProducts, userPendingProducts, userClosedProducts } from '../../../../services/user';
import { closeProduct, updateProductStatus } from '../../../../services/product';
import { Avatar, message, Badge, Input, Form } from 'antd';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MUIButton, TextField, IconButton, InputAdornment, Pagination, Modal, Box } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { UserOutlined, EditOutlined, LockOutlined, UnlockOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import UnifiedDashboard from '../../../../components/layout/unified-dashboard';
import ProductCard from '../../../../components/product/product-card';
import UpsertProduct from '../../../../components/product/upsert';
import { Tabs as MUITabs, Tab, Badge as MUIBadge } from '@mui/material';

const { Search } = Input;

const ManageUsers = () => {
  const countPerPage = 10;
  const [users, setUsers] = useState({
    userList: [],
    pagination: [],
    deleted: false,
    banned: false,
  });
  const [current, setCurrent] = useState();
  const [search, setSearch] = useState('');
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banDialog, setBanDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedUserForBan, setSelectedUserForBan] = useState(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [passwordForm] = Form.useForm();
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userProducts, setUserProducts] = useState({});
  const [expandedFilters, setExpandedFilters] = useState({});
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState(null);
  const [editProductModal, setEditProductModal] = useState(false);
  const [selectedProductForEdit, setSelectedProductForEdit] = useState(null);

  const toggleUserExpand = async (userId) => {
    setSelectedUserForModal(userId);
    setUserModalOpen(true);

    if (!userProducts[userId]) {
      try {
        const [activeRes, pendingRes, closedRes] = await Promise.all([
          userActiveProducts(userId),
          userPendingProducts(userId),
          userClosedProducts(userId)
        ]);
        setUserProducts({
          ...userProducts,
          [userId]: {
            active: activeRes.data,
            pending: pendingRes.data,
            closed: closedRes.data
          }
        });
      } catch (err) {
        console.log(err);
        message.error('Erro ao carregar produtos do usuário', 4);
      }
    }
  };

  const handleEditProductClose = async () => {
    setEditProductModal(false);
    setSelectedProductForEdit(null);
    // Reload user products if editing from user modal
    if (selectedUserForModal) {
      try {
        const [activeRes, pendingRes, closedRes] = await Promise.all([
          userActiveProducts(selectedUserForModal),
          userPendingProducts(selectedUserForModal),
          userClosedProducts(selectedUserForModal)
        ]);
        setUserProducts({
          ...userProducts,
          [selectedUserForModal]: {
            active: activeRes.data,
            pending: pendingRes.data,
            closed: closedRes.data
          }
        });
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleClose = async (productId, userId) => {
    try {
      await closeProduct(productId);
      message.success('Produto encerrado com sucesso');
      // Reload products for this user
      const [activeRes, pendingRes, closedRes] = await Promise.all([
        userActiveProducts(userId),
        userPendingProducts(userId),
        userClosedProducts(userId)
      ]);
      setUserProducts({
        ...userProducts,
        [userId]: {
          active: activeRes.data,
          pending: pendingRes.data,
          closed: closedRes.data
        }
      });
    } catch (err) {
      message.error('Erro ao encerrar produto');
    }
  };

  const handleStatusChange = async (e, productId, userId) => {
    const newStatus = e.target.value;
    setStatusChangeLoading(productId);
    try {
      await updateProductStatus({
        productId,
        status: newStatus,
        token: token,
      });
      message.success('Status atualizado com sucesso');
      
      // Reload products for this user
      const [activeRes, pendingRes, closedRes] = await Promise.all([
        userActiveProducts(userId),
        userPendingProducts(userId),
        userClosedProducts(userId)
      ]);
      setUserProducts({
        ...userProducts,
        [userId]: {
          active: activeRes.data,
          pending: pendingRes.data,
          closed: closedRes.data
        }
      });
    } catch (err) {
      message.error('Erro ao atualizar status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  const getPaginatedUsers = () => {
    const to = current * countPerPage;
    const from = to - countPerPage;
    return userList.slice(from, to);
  };

  const { userList, pagination, deleted, banned } = users;
  const { user, token } = isAuthenticated();
  const history = useHistory();

  const searchData = useRef(
    throttle((val) => {
      const query = val.toLowerCase();
      setUsers({
        ...users,
        pagination: userList.filter((c) => {
          return c.name.toLowerCase().indexOf(query) > -1;
        })
      });
    }, 400)
  );

  useEffect(() => {
    if (!search) {
      loadUsers(1);
    } else {
      searchData.current(search);
    }
  }, [search, deleted, banned]);

  const loadUsers = async (page) => {
    const res = await getUsers();
    setCurrent(page);
    const to = page * countPerPage;
    const from = to - countPerPage;
    setUsers({ userList: res.data, pagination: res.data.slice(from, to) });
  };

  const handleBan = async (userId) => {
    try {
      const adminRole = user.role;
      await banUser(userId, { adminRole, token });
      message.success('Usuário banido', 4);
      setUsers({ ...users, banned: false ? true : false });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const handleUnBan = async (userId) => {
    try {
      const adminRole = user.role;
      await unBanUser(userId, { adminRole, token });
      message.success('Usuário desbanido', 4);
      setUsers({ ...users, banned: true ? false : true });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const adminRole = user.role;
      await deleteUser(userId, { adminRole, token });
      message.success('Usuário deletado', 4);
      setUsers({ ...users, deleted: false ? true : false });
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) message.error(err.response.data?.error || err.response.data, 4);
    }
  };

  const openChangePasswordModal = (userItem) => {
    setSelectedUser(userItem);
    setChangePasswordModal(true);
    passwordForm.resetFields();
  };

  const handleChangePassword = async (values) => {
    try {
      setLoadingPassword(true);
      await changeUserPassword(selectedUser._id, {
        password: values.password,
        newPasswordConfirm: values.newPasswordConfirm
      });
      message.success('Senha alterada com sucesso', 4);
      setChangePasswordModal(false);
      passwordForm.resetFields();
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) {
        message.error(err.response.data?.error || err.response.data, 4);
      } else {
        message.error('Erro ao alterar senha', 4);
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  const renderUserCard = (userItem, index) => {
    const c = userItem;
    const api = process.env.REACT_APP_API?.replace(/\/api$/, '');
    
    return (
      <div key={index} className='manage-users-user-card'>
        <div className='manage-users-card-header' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className='manage-users-user-header'>
            <Avatar 
              size={60}
              src={c.photo ? `${api}${c.photo}` : null}
              icon={<UserOutlined />}
              className="manage-users-avatar"
            />
            <div className="manage-users-user-details">
              <Link to={`/user/${c._id}`} className='manage-users-user-name'>
                {c.name}
              </Link>
              {c.role === 'banned' && (
                <Badge status="error" text="Banido" className='manage-users-badge' />
              )}
              <div className='manage-users-user-meta'>
                <small><i className='fas fa-envelope'></i> {c.email}</small>
              </div>
              <div className='manage-users-user-meta'>
                <small><i className='fas fa-calendar'></i> {moment(c.createdAt).format('DD/MM/YYYY')}</small>
              </div>
            </div>
          </div>
          <button
            style={{ 
              padding: '8px', 
              width: '40px', 
              height: '40px',
              borderRadius: '50%', 
              border: 'none', 
              backgroundColor: '#f0f0f0', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              transition: 'all 0.2s',
              color: '#666'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e0e0e0'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onClick={() => toggleUserExpand(c._id)}
            title="Ver produtos"
          >
            <EyeOutlined />
          </button>
        </div>

        <div className='manage-users-card-actions'>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', width: '100%' }}>
            <button 
              className='manage-users-action-btn manage-users-btn-edit'
              onClick={() => history.push(`/admin/user/edit/${c._id}`)}
              title="Editar usuário"
              style={{ flex: 1 }}
            >
              <EditOutlined /> Editar
            </button>
            <button 
              className='manage-users-action-btn manage-users-btn-product' 
              onClick={() => history.push(`/admin/add-product/${c._id}`)}
              title="Adicionar produto"
              style={{ flex: 1 }}
            >
              <PlusOutlined /> Produto
            </button>
            <button
              className='manage-users-action-btn manage-users-btn-password'
              onClick={() => openChangePasswordModal(c)}
              title="Trocar senha"
              style={{ flex: 1 }}
            >
              <LockOutlined /> Senha
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
            {c.role === 'banned' ? (
              <button 
                className='manage-users-action-btn manage-users-btn-unban'
                onClick={() => { setSelectedUserForBan(c); setBanDialog(true); }}
                title="Desbanir usuário"
                style={{ flex: 1 }}
              >
                <UnlockOutlined /> Desbanir
              </button>
            ) : (
              <button
                className='manage-users-action-btn manage-users-btn-ban'
                onClick={() => { setSelectedUserForBan(c); setBanDialog(true); }}
                title="Banir usuário"
                style={{ flex: 1 }}
              >
                <LockOutlined /> Banir
              </button>
            )}
            <button
              className='manage-users-action-btn manage-users-btn-delete'
              onClick={() => { setSelectedUserForDelete(c); setDeleteDialog(true); }}
              title="Deletar usuário"
              style={{ flex: 1 }}
            >
              <DeleteOutlined /> Deletar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <UnifiedDashboard>
      <div className="manage-users-container">
        <div className='manage-users-header'>
          <div className='manage-users-header-content'>
            <h2 className='manage-users-title'>
              <i className='fas fa-users'></i>
              Gerenciar Usuários
            </h2>
            <p className='manage-users-subtitle'>Administre usuários do sistema</p>
          </div>
          <button 
            className='manage-users-add-user-btn'
            onClick={() => history.push('/admin/user/edit/new')}
            title="Cadastrar novo usuário"
          >
            <PlusOutlined /> Cadastrar Usuário
          </button>
        </div>

        <div className='manage-users-search-wrapper'>
          <div className='search-bar-container'>
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-bar-input"
            />
            <button className='search-bar-button' title="Buscar">
              <SearchOutlined />
            </button>
          </div>
        </div>

        <div className="manage-users-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          {getPaginatedUsers().map((user, index) => renderUserCard(user, index))}
        </div>

        {userList.length > countPerPage && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
              count={Math.ceil(userList.length / countPerPage)}
              page={current}
              onChange={(e, page) => setCurrent(page)}
              color="primary"
              size="medium"
            />
          </div>
        )}
      </div>

      <Dialog
        open={changePasswordModal}
        onClose={() => {
          setChangePasswordModal(false);
          passwordForm.resetFields();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Trocar senha - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handleChangePassword}
            className="manage-users-password-form"
          >
            <Form.Item
              label="Nova Senha"
              name="password"
              rules={[
                { required: true, message: 'Senha é obrigatória' },
                { min: 6, message: 'Senha deve ter no mínimo 6 caracteres' },
                {
                  pattern: /\d/,
                  message: 'Senha deve conter pelo menos um número'
                }
              ]}
            >
              <TextField 
                type={showPassword ? "text" : "password"} 
                placeholder="Digite a nova senha" 
                variant="outlined" 
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Form.Item>
            <Form.Item
              label="Confirmar Senha"
              name="newPasswordConfirm"
              rules={[
                { required: true, message: 'Confirmação de senha é obrigatória' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('As senhas não correspondem'));
                  },
                }),
              ]}
            >
              <TextField 
                type={showConfirmPassword ? "text" : "password"} 
                placeholder="Confirme a nova senha" 
                variant="outlined" 
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Form.Item>
          </Form>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={() => { setChangePasswordModal(false); passwordForm.resetFields(); }}>Cancelar</MUIButton>
          <MUIButton onClick={() => passwordForm.submit()} disabled={loadingPassword}>Alterar</MUIButton>
        </DialogActions>
      </Dialog>

      {/* Ban Dialog */}
      <Dialog
        open={banDialog}
        onClose={() => setBanDialog(false)}
      >
        <DialogTitle>{selectedUserForBan?.role === 'banned' ? 'Desbanir usuário' : 'Banir usuário'}</DialogTitle>
        <DialogContent>
          <p>Tem certeza que deseja {selectedUserForBan?.role === 'banned' ? 'desbanir' : 'banir'} {selectedUserForBan?.name}?</p>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={() => setBanDialog(false)}>Cancelar</MUIButton>
          <MUIButton onClick={() => { 
            if (selectedUserForBan?.role === 'banned') {
              handleUnBan(selectedUserForBan._id);
            } else {
              handleBan(selectedUserForBan._id);
            }
            setBanDialog(false);
          }}>Confirmar</MUIButton>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Deletar usuário</DialogTitle>
        <DialogContent>
          <p>Tem certeza que deseja deletar {selectedUserForDelete?.name}? Esta ação não pode ser desfeita.</p>
        </DialogContent>
        <DialogActions>
          <MUIButton onClick={() => setDeleteDialog(false)}>Cancelar</MUIButton>
          <MUIButton onClick={() => { 
            handleDelete(selectedUserForDelete._id);
            setDeleteDialog(false);
          }}>Deletar</MUIButton>
        </DialogActions>
      </Dialog>

      {/* User Products Modal */}
      <Modal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{ 
          width: '1000px', 
          height: '600px', 
          backgroundColor: 'white', 
          borderRadius: 2, 
          p: 3, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {selectedUserForModal && (
            <>
              <h4>Produtos de {userList.find(u => u._id === selectedUserForModal)?.name}</h4>
              {userProducts[selectedUserForModal] ? (
                <>
                  <MUITabs 
                    value={expandedFilters[selectedUserForModal] || 'active'} 
                    onChange={(e, newValue) => setExpandedFilters({ ...expandedFilters, [selectedUserForModal]: newValue })}
                    className='dashboard-tabs'
                    style={{ marginBottom: '20px' }}
                  >
                    <Tab 
                      label={
                        <MUIBadge badgeContent={userProducts[selectedUserForModal].active.length} color="primary">
                          <span>Ativos</span>
                        </MUIBadge>
                      } 
                      value='active' 
                    />
                    <Tab 
                      label={
                        <MUIBadge badgeContent={userProducts[selectedUserForModal].pending.length} color="primary">
                          <span>Pendentes</span>
                        </MUIBadge>
                      } 
                      value='pending' 
                    />
                    <Tab 
                      label={
                        <MUIBadge badgeContent={userProducts[selectedUserForModal].closed.length} color="primary">
                          <span>Encerrados</span>
                        </MUIBadge>
                      } 
                      value='closed' 
                    />
                  </MUITabs>

                  <div className="user-dashboard-grid" style={{ marginTop: '10px' }}>
                    {(userProducts[selectedUserForModal][expandedFilters[selectedUserForModal] || 'active'] || []).map((product, idx) => (
                      <ProductCard 
                        key={idx} 
                        product={product} 
                        onStatusChange={(e) => handleStatusChange(e, product._id, selectedUserForModal)}
                        onClose={() => handleClose(product._id, selectedUserForModal)}
                        onEdit={(productId) => {
                          setSelectedProductForEdit(productId);
                          setEditProductModal(true);
                        }}
                        showActions={true}
                        showAuthor={false}
                        isAdmin={true}
                        statuses={['active', 'pending', 'closed']}
                        statusChangeLoading={statusChangeLoading}
                      />
                    ))}
                    {(userProducts[selectedUserForModal][expandedFilters[selectedUserForModal] || 'active'] || []).length === 0 && (
                      <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
                        Nenhum produto encontrado
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <i className='fas fa-spinner fa-spin' style={{ fontSize: '24px' }}></i>
                  <p>Carregando produtos...</p>
                </div>
              )}
            </>
          )}
        </Box>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        open={editProductModal}
        onClose={() => setEditProductModal(false)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{ 
          width: '90%', 
          height: '90%', 
          backgroundColor: 'white', 
          borderRadius: 2, 
          p: 3, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {selectedProductForEdit && (
            <UpsertProduct
              isModal={true}
              productId={selectedProductForEdit}
              onClose={handleEditProductClose}
              hideBackButton={true}
            />
          )}
        </Box>
      </Modal>
    </UnifiedDashboard>
  );
};

export default ManageUsers;
